import React from 'react';
import { Box, Typography } from '@mui/material';
import { isCasualLeaveRecord } from '../../../utils/casualLeaveUtils';
import { getCurrentQuarterCasualStats } from '../../../services/CasualLeaveAccountingService';

function QuotaDetails(props) {
  const {
    leaveQuota,
    externalHoursQuota,
    clientOfficeHoursQuota,
    workTravelHoursQuota,
    wfhQuota,
    externalHours,
    clientOfficeHours,
    workTravelHours,
    wfhDays,
    type,
    startDate,
    casualLeaveAccounting,
  } = props;

  const getMonthlyEntitledHours = (quota, startDate) => {
    const month = new Date(startDate).getMonth();
    return Number(quota?.['thisYear']?.[month]) || 0;
  };

  const getQuotaForYear = (quota, startDate) => {
    const entitled = getMonthlyEntitledHours(quota, startDate);
    return entitled > 0 ? `${entitled} hrs 00 min` : '0 min';
  };

  const getQuotaForQuarter = (quota, startDate) => {
    const month = new Date(startDate).getMonth();
    const quarterIndex = Math.floor(month / 3);
    const quarterQuota = quota?.['thisYear']?.[quarterIndex] || 0;
    return quarterQuota > 0 ? `${quarterQuota} days` : '0 days';
  };

  const externalEntitledHours = getMonthlyEntitledHours(
    externalHoursQuota?.externalHoursQuota,
    startDate,
  );
  const clientOfficeEntitledHours = getMonthlyEntitledHours(
    clientOfficeHoursQuota?.clientOfficeHoursQuota,
    startDate,
  );
  const workTravelEntitledHours = getMonthlyEntitledHours(
    workTravelHoursQuota?.workTravelHoursQuota,
    startDate,
  );

  const isCasualWithAccounting = (leave) =>
    isCasualLeaveRecord(leave, leave?.leaveName, casualLeaveAccounting?.leaveId);

  const casualStats = getCurrentQuarterCasualStats(casualLeaveAccounting);

  return (
    <Box>
      <Box>
        <Box
          sx={{ justifyContent: 'center', textAlign: 'center', mt: 2, mb: 4 }}
        >
          <h5>
            <strong>Quota</strong>
          </h5>
        </Box>
      </Box>
      {leaveQuota.length !== 0 &&
        leaveQuota?.map((leave, index) => {
          if (isCasualWithAccounting(leave)) {
            const quarterLabel = casualStats?.quarter
              ? `Q${casualStats.quarter}`
              : 'Quarterly';
            return (
              <Box key={leave?.leaveId || index} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    mt: 2,
                    mb: 1,
                    ml: 1,
                  }}
                >
                  <Typography variant="h7" sx={{ width: '10rem' }}>
                    {leave?.leaveName}
                  </Typography>
                  {casualLeaveAccounting ? (
                    <>
                      <div className="leave-stat-container">
                        <div className="leave-number available-leave">
                          <Typography align="center" component="div">
                            {casualStats?.entitled ?? 0}
                          </Typography>
                        </div>
                        <div className="leave-stats">{quarterLabel} Entitled</div>
                      </div>
                      <div className="leave-stat-container">
                        <div className="leave-number used-leave">
                          <Typography align="center" component="div">
                            {casualStats?.availed ?? 0}
                          </Typography>
                        </div>
                        <div className="leave-stats">{quarterLabel} Availed</div>
                      </div>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Quarterly Casual Leave accounting is being initialized.
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          }
          if (leave?.totalAllowedLeaves === 0) {
            return null;
          }
          return (
            <Box key={leave?.leaveId || index} sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  mt: 2,
                  mb: 1,
                  ml: 1,
                }}
              >
                <Typography variant="h7" sx={{ width: '10rem' }}>
                  {leave?.leaveName}
                </Typography>
                <div className="leave-stat-container">
                  <div className="leave-number available-leave">
                    <Typography align="center" component="div">
                      {leave?.totalAllowedLeaves}
                    </Typography>
                  </div>
                  <div className="leave-stats">Yearly Entitled</div>
                </div>

                <div className="leave-stat-container">
                  <div className="leave-number used-leave">
                    <Typography align="center" component="div">
                      {leave?.usedLeaves}
                    </Typography>
                  </div>
                  <div className="leave-stats">Availed</div>
                </div>
              </Box>
            </Box>
          );
        })}
      <>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            mt: 2,
            mb: 3,
            ml: 1,
          }}
        >
          <Typography variant="h7" sx={{ width: '10rem' }}>
            WFH Days
          </Typography>
          <div className="leave-stat-container">
            <div className="leave-number available-leave">
              <Typography align="center" component="div">
                {getQuotaForQuarter(wfhQuota?.quotaWFH, startDate)}
              </Typography>
            </div>
            <div className="leave-stats">Quarterly Entitled</div>
          </div>

          <div className="leave-stat-container">
            <div className="leave-number used-leave">
              <Typography align="center" component="div">
                {wfhDays ? `${wfhDays}` : '0 days'}
              </Typography>
            </div>
            <div className="leave-stats">Availed</div>
          </div>
        </Box>
        {externalEntitledHours > 0 && (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              mt: 2,
              mb: 2,
              ml: 1,
            }}
          >
            <Typography variant="h7" sx={{ width: '10rem' }}>
              External Hours
            </Typography>
            <div className="leave-stat-container">
              <div className="leave-number available-leave">
                <Typography align="center" component="div">
                  {getQuotaForYear(
                    externalHoursQuota?.externalHoursQuota,
                    startDate,
                  )}
                </Typography>
              </div>
              <div className="leave-stats">Monthly Entitled</div>
            </div>

            <div className="leave-stat-container">
              <div className="leave-number used-leave">
                <Typography align="center" component="div">
                  {externalHours ? `${externalHours}` : '0 min'}
                </Typography>
              </div>
              <div className="leave-stats">Availed</div>
            </div>
          </Box>
        )}
        {clientOfficeEntitledHours > 0 && (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              mt: 2,
              mb: 2,
              ml: 1,
            }}
          >
            <Typography variant="h7" sx={{ width: '10rem' }}>
              Client Office Hours
            </Typography>
            <div className="leave-stat-container">
              <div className="leave-number available-leave">
                <Typography align="center" component="div">
                  {getQuotaForYear(
                    clientOfficeHoursQuota?.clientOfficeHoursQuota,
                    startDate,
                  )}
                </Typography>
              </div>
              <div className="leave-stats">Monthly Entitled</div>
            </div>

            <div className="leave-stat-container">
              <div className="leave-number used-leave">
                <Typography align="center" component="div">
                  {clientOfficeHours ? `${clientOfficeHours}` : '0 min'}
                </Typography>
              </div>
              <div className="leave-stats">Availed</div>
            </div>
          </Box>
        )}
        {workTravelEntitledHours > 0 && (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              mt: 2,
              mb: 2,
              ml: 1,
            }}
          >
            <Typography variant="h7" sx={{ width: '10rem' }}>
              Work Travel Hours
            </Typography>
            <div className="leave-stat-container">
              <div className="leave-number available-leave">
                <Typography align="center" component="div">
                  {getQuotaForYear(
                    workTravelHoursQuota?.workTravelHoursQuota,
                    startDate,
                  )}
                </Typography>
              </div>
              <div className="leave-stats">Monthly Entitled</div>
            </div>

            <div className="leave-stat-container">
              <div className="leave-number used-leave">
                <Typography align="center" component="div">
                  {workTravelHours ? `${workTravelHours}` : '0 min'}
                </Typography>
              </div>
              <div className="leave-stats">Availed</div>
            </div>
          </Box>
        )}
      </>
    </Box>
  );
}

export default QuotaDetails;
